import React, { useState, useEffect, useMemo } from 'react'
import { FaSearch } from 'react-icons/fa'
import { FunnelIcon, TableCellsIcon, ArrowDownOnSquareStackIcon } from '@heroicons/react/24/outline'
import { NavLink, useNavigate } from 'react-router-dom'
import { obtenerBookings } from '../../api'
import { useUser } from '../context/AuthContext'

const TABS = [
  { key: 'ordered', label: 'ORDERED' },
  { key: 'delivered', label: 'CONFIRMED' },
  { key: 'cancelled', label: 'CANCELLED' }
]

const ALL_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'reference', label: 'Reference' },
  { key: 'client', label: 'Customer' },
  { key: 'tipo', label: 'Type' },
  { key: 'items', label: 'Nb Items' },
  { key: 'total', label: 'Total' },
];

const Bookings = () => {
  const [search, setSearch] = useState('')
  // Export to CSV
  function exportToCSV() {
    if (!filtered.length) return;
    const cols = ALL_COLUMNS.filter(col => visibleColumns.includes(col.key));
    const header = cols.map(col => col.label).join(',');
    const rowsData = filtered.map(b =>
      cols.map(col => {
        let val = b[col.key];
        if (typeof val === 'string') {
          val = val.replace(/"/g, '""');
          if (val.includes(',') || val.includes('"')) val = `"${val}"`;
        }
        return val;
      }).join(',')
    );
    const csv = [header, ...rowsData].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings_export_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  const [visibleColumns, setVisibleColumns] = useState(ALL_COLUMNS.map(c => c.key))
  const [showColumnSelector, setShowColumnSelector] = useState(false)
  const [dateFilter, setDateFilter] = useState('')
  const [activeTab, setActiveTab] = useState('ordered')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const { logout } = useUser()
  const navigate = useNavigate()

  useEffect(() => {
    obtenerBookings()
      .then(data => setRows(data))
      .catch(err => {
        if (err.response?.status === 401) logout()
        console.error(err)
      })
      .finally(() => setLoading(false))
  }, [logout])

  const filtered = useMemo(() => {
    const list = Array.isArray(rows) ? rows : []
    return list.filter(b => {
      if (b.state !== activeTab) return false
      if (dateFilter) {
        // date is in format dd/mm/yyyy, dateFilter is yyyy-mm-dd
        const [d, m, y] = b.date.split(',')[0].split('/')
        const bookingDate = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
        if (bookingDate !== dateFilter) return false
      }
      if (search) {
        const q = search.toLowerCase()
        return b.reference.toLowerCase().includes(q) || b.client.toLowerCase().includes(q)
      }
      return true
    })
  }, [rows, activeTab, search, dateFilter])

  const counts = useMemo(() => rows.reduce((acc, b) => { acc[b.state] = (acc[b.state] || 0) + 1; return acc }, {}), [rows])

  if (loading) return <div className="p-6 text-[rgb(var(--color-text))]">Loading bookings…</div>

  return (
    <div className="flex-1 p-6 bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))] h-full overflow-auto">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-4">
        <div className="flex gap-4 items-center">
          <div className="relative w-64">
            <FaSearch className="absolute left-3 top-2 text-[rgb(var(--color-text))]/50" />
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 pr-3 py-2 w-full bg-hover-bg rounded placeholder-[rgb(var(--color-text))]/50 text-primary"
            />
          </div>
          <button
            className="px-3 py-2 bg-primary text-white rounded hover:bg-primary/80 text-xs"
            onClick={() => setDateFilter(new Date().toISOString().slice(0, 10))}
          >
            Today
          </button>
          <input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="px-2 py-2 rounded bg-hover-bg text-primary border border-gray-300 text-xs"
          />
          {dateFilter && (
            <button className="ml-1 text-xs text-red-400 hover:underline" onClick={() => setDateFilter('')}>Clear</button>
          )}
        </div>
        <div className="flex items-center space-x-6 text-sm uppercase relative">
          <button className="flex items-center space-x-1 hover:text-primary"><FunnelIcon className="h-5 w-5" /><span>Filter</span></button>
          <button
            className="flex items-center space-x-1 hover:text-primary"
            onClick={() => setShowColumnSelector(v => !v)}
          >
            <TableCellsIcon className="h-5 w-5" /><span>Columns</span>
          </button>
          {showColumnSelector && (
            <div className="absolute right-0 top-8 bg-white text-black rounded shadow-lg p-4 z-50 min-w-[180px]">
              <div className="font-bold mb-2">Select columns</div>
              {ALL_COLUMNS.map(col => (
                <label key={col.key} className="flex items-center gap-2 mb-1">
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(col.key)}
                    onChange={e => {
                      if (e.target.checked) {
                        setVisibleColumns(cols => [...cols, col.key])
                      } else {
                        setVisibleColumns(cols => cols.filter(k => k !== col.key))
                      }
                    }}
                  />
                  {col.label}
                </label>
              ))}
              <button className="mt-2 text-xs text-primary hover:underline" onClick={() => setShowColumnSelector(false)}>Close</button>
            </div>
          )}
          <button className="flex items-center space-x-1 hover:text-primary" onClick={exportToCSV}><ArrowDownOnSquareStackIcon className="h-5 w-5" /><span>Export</span></button>
        </div>
      </div>
      <div className="flex space-x-6 mb-4 uppercase text-sm border-b border-[rgb(var(--color-border))]">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`pb-2 ${activeTab === tab.key ? 'border-b border-primary text-primary' : 'text-[rgb(var(--color-text))]/60 hover:text-primary'}`}>
            {tab.label} ({counts[tab.key] || 0})
          </button>
        ))}
      </div>
      <table className="w-full table-auto">
        <thead className="bg-hover-bg text-[rgb(var(--color-text))]/80 text-sm uppercase">
          <tr>
            <th className="px-4 py-2"><input type="checkbox" /></th>
            {ALL_COLUMNS.filter(col => visibleColumns.includes(col.key)).map(col => (
              <th
                key={col.key}
                className={col.key === 'items' || col.key === 'total' ? 'px-4 py-2 text-right' : 'px-4 py-2 text-left'}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map(b => (
            <tr
              key={b.id}
              className="border-b border-[rgb(var(--color-border))] hover:bg-hover-bg cursor-pointer"
              onDoubleClick={() => navigate(`/admin/bookings/${b.id}`)}
            >
              <td className="px-4 py-2"><input type="checkbox" /></td>
              {visibleColumns.includes('date') && <td className="px-4 py-2">{b.date}</td>}
              {visibleColumns.includes('reference') && <td className="px-4 py-2">{b.reference}</td>}
              {visibleColumns.includes('client') && (
                <td className="px-4 py-2">
                  <NavLink to=''>{b.client}</NavLink>
                </td>
              )}
              {visibleColumns.includes('tipo') && (
                <td className="px-4 py-2 text-left">
                  {b.tipo === 'privada' && <span className="text-gray-400">Private</span>}
                  {b.tipo === 'publica' && <span className="text-green-500 font-bold">Public Room</span>}
                  {b.tipo === 'join-publica' && <span className="text-blue-500 font-bold">Join Public Room</span>}
                </td>
              )}
              {visibleColumns.includes('items') && <td className="px-4 py-2 text-right">{b.items}</td>}
              {visibleColumns.includes('total') && <td className="px-4 py-2 text-right">{b.total}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
export default Bookings
